import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Glasses, MessageCircle, Camera, ImageIcon, Loader2, X, Sparkles, MapPin, Clock, ShoppingBag, Share2, Search, Plus, Trash2, Download, ChevronLeft, Check, Store } from "lucide-react";
import { logDebug } from "@/lib/logger";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;
const fmtBRL = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(v) || 0);
const descontoPct = (p) => (p.preco_promocional && p.preco_promocional < p.preco ? Math.round((1 - p.preco_promocional / p.preco) * 100) : 0);
const precoFinal = (p) => (descontoPct(p) > 0 ? p.preco_promocional : p.preco);
const waLink = (tel, msg) => `https://wa.me/${(tel || "").replace(/\D/g, "")}?text=${encodeURIComponent(msg)}`;

function ordenarCategorias(cats, genero) {
  if (!genero || genero === "nd") return cats;
  const sel = genero === "masc" ? "masculino" : "feminino";
  const opp = genero === "masc" ? "feminino" : "masculino";
  const has = (n, g) => (n || "").toLowerCase().includes(g);
  return [
    ...cats.filter((c) => has(c.nome, sel)),
    ...cats.filter((c) => has(c.nome, opp)),
    ...cats.filter((c) => !has(c.nome, "masculino") && !has(c.nome, "feminino")),
  ];
}

/* ---------------- Lead Gate ---------------- */
function LeadGate({ loja, data, onSalvar }) {
  const [nome, setNome] = useState("");
  const [tel, setTel] = useState("");
  const [saving, setSaving] = useState(false);
  const salvar = async () => {
    if (!nome.trim() || tel.replace(/\D/g, "").length < 8) return;
    setSaving(true);
    try { await onSalvar(nome.trim(), tel.trim()); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/95 flex items-center justify-center p-5" data-testid="lead-gate">
      <div className="bg-white rounded-2xl p-7 w-full max-w-sm shadow-2xl text-center">
        <div className="w-16 h-16 rounded-full bg-slate-900 text-white flex items-center justify-center mx-auto mb-4"><Glasses className="w-8 h-8" /></div>
        <h2 className="text-xl font-extrabold text-slate-900">{data?.empresa ? `${data.empresa} · ` : ""}{loja}</h2>
        <p className="text-sm text-slate-500 mt-1 mb-5">Bem-vindo! Informe seus dados para ver nosso catálogo exclusivo.</p>
        <input value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Seu nome" data-testid="lead-nome"
          className="w-full px-4 py-3 rounded-xl border border-slate-200 mb-3 text-sm focus:border-slate-900 outline-none" />
        <input value={tel} onChange={(e) => setTel(e.target.value)} placeholder="Seu WhatsApp (com DDD)" inputMode="tel" data-testid="lead-telefone"
          className="w-full px-4 py-3 rounded-xl border border-slate-200 mb-4 text-sm focus:border-slate-900 outline-none" />
        <button onClick={salvar} disabled={saving || !nome.trim() || tel.replace(/\D/g, "").length < 8} data-testid="lead-entrar"
          className="w-full py-3.5 bg-slate-900 hover:bg-slate-800 disabled:opacity-40 text-white font-bold rounded-xl transition-transform active:scale-[0.98]">
          {saving ? "Entrando..." : "Ver catálogo"}
        </button>
        <p className="text-[10px] text-slate-400 mt-3">Usaremos apenas para melhorar seu atendimento.</p>
      </div>
    </div>
  );
}

/* ---------------- Gender Select ---------------- */
function GeneroSelect({ data, onEscolher }) {
  const opts = [
    { id: "masc", label: "Modelos Masculinos", emoji: "M" },
    { id: "fem", label: "Modelos Femininos", emoji: "F" },
    { id: "nd", label: "Catálogo completo", emoji: "✦" },
  ];
  return (
    <div className="h-[100dvh] overflow-y-auto bg-slate-50 flex flex-col items-center justify-center p-6" data-testid="genero-select">
      <div className="w-20 h-20 rounded-full bg-slate-900 text-white flex items-center justify-center mb-4"><Glasses className="w-10 h-10" /></div>
      <h1 className="text-2xl font-extrabold text-slate-900 text-center">{data.empresa ? `${data.empresa} · ` : ""}{data.loja}</h1>
      {(data.horario || data.endereco) && (
        <div className="flex flex-wrap items-center justify-center gap-3 text-xs text-slate-500 mt-2 mb-1">
          {data.horario && <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{data.horario}</span>}
          {data.endereco && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{data.endereco}</span>}
        </div>
      )}
      <p className="text-sm text-slate-500 mt-4 mb-6">O que você procura hoje?</p>
      <div className="w-full max-w-sm space-y-3">
        {opts.map((o) => (
          <button key={o.id} onClick={() => onEscolher(o.id)} data-testid={`genero-${o.id}`}
            className="w-full py-4 bg-white border border-slate-200 rounded-2xl font-semibold text-slate-800 hover:border-slate-900 hover:shadow-md transition-all flex items-center gap-4 px-5 active:scale-[0.99]">
            <span className="w-10 h-10 rounded-full bg-slate-900 text-white flex items-center justify-center text-sm font-bold shrink-0">{o.emoji}</span>
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Product Card ---------------- */
function ProductCard({ p, noCarrinho, onProvar, onAbrir, onCart, onWhats, provadorOn }) {
  const d = descontoPct(p);
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-all p-3 flex flex-col" data-testid="product-card">
      <button onClick={() => onAbrir(p)} className="relative aspect-square bg-white rounded-lg overflow-hidden flex items-center justify-center mb-2" data-testid="product-card-open">
        {p.foto_url ? <img src={`${BACKEND}${p.foto_url}`} alt={p.nome} className="w-full h-full object-contain" loading="lazy" decoding="async" /> : <ImageIcon className="w-10 h-10 text-slate-200" />}
        {d > 0 && <span className="absolute top-2 left-2 text-xs font-bold bg-red-500 text-white px-2 py-0.5 rounded-full" data-testid="product-card-promo-badge">-{d}%</span>}
      </button>
      <h3 className="text-sm font-semibold text-slate-800 line-clamp-2 min-h-[2.5rem] cursor-pointer" onClick={() => onAbrir(p)} data-testid="product-card-title">{p.nome}</h3>
      <div className="flex items-baseline gap-1.5 mt-1" data-testid="product-card-price">
        {d > 0 && <span className="text-xs text-slate-400 line-through">{fmtBRL(p.preco)}</span>}
        <span className="text-lg font-extrabold text-emerald-600">{fmtBRL(precoFinal(p))}</span>
      </div>
      <div className="mt-2.5 flex flex-col gap-1.5">
        {provadorOn && p.foto_url && (
          <button onClick={() => onProvar(p)} data-testid="button-virtual-tryon"
            className="w-full py-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold rounded-lg flex items-center justify-center gap-1.5 active:scale-[0.98]"><Sparkles className="w-3.5 h-3.5" /> Provar com IA</button>
        )}
        <div className="flex gap-1.5">
          <button onClick={() => onCart(p)} data-testid="button-add-cart" className="flex-1 py-2 border border-slate-300 hover:border-slate-900 text-slate-700 text-xs font-bold rounded-lg flex items-center justify-center gap-1 active:scale-[0.98]"><Plus className="w-3.5 h-3.5" /> Carrinho</button>
          <button onClick={() => onWhats(p)} data-testid="button-buy-whatsapp" className="flex-1 py-2 text-white text-xs font-bold rounded-lg flex items-center justify-center gap-1 active:scale-[0.98]" style={{ backgroundColor: "#25D366" }}><MessageCircle className="w-3.5 h-3.5" /> Comprar</button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Product Detail ---------------- */
function ProductDetail({ p, onClose, onProvar, onCart, onWhats, provadorOn }) {
  const [imgAtiva, setImgAtiva] = useState(p.foto_url);
  const d = descontoPct(p);
  const extras = p.fotos_extras_urls || [];
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl max-h-[94vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="product-detail">
        <div className="sticky top-0 bg-white flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <button onClick={onClose} className="text-slate-500 flex items-center gap-1 text-sm" data-testid="product-detail-back"><ChevronLeft className="w-5 h-5" /> Voltar</button>
          {d > 0 && <span className="text-xs font-bold bg-red-500 text-white px-2 py-0.5 rounded-full">-{d}%</span>}
        </div>
        <div className="p-5">
          <div className="aspect-square bg-white rounded-xl border border-slate-100 flex items-center justify-center overflow-hidden mb-3">
            {imgAtiva ? <img src={`${BACKEND}${imgAtiva}`} alt={p.nome} className="w-full h-full object-contain" /> : <ImageIcon className="w-12 h-12 text-slate-200" />}
          </div>
          {extras.length > 0 && (
            <div className="flex gap-2 overflow-x-auto mb-4">
              {[p.foto_url, ...extras].map((u, i) => (
                <button key={u || i} onClick={() => setImgAtiva(u)} className={`w-14 h-14 rounded-lg border-2 overflow-hidden shrink-0 ${imgAtiva === u ? "border-slate-900" : "border-slate-200"}`}>
                  <img src={`${BACKEND}${u}`} alt="" className="w-full h-full object-contain" />
                </button>
              ))}
            </div>
          )}
          {p.categoria && <p className="text-xs uppercase tracking-wide text-slate-400">{p.categoria}</p>}
          <h2 className="text-xl font-extrabold text-slate-900">{p.nome}</h2>
          <div className="flex items-baseline gap-2 mt-1 mb-3">
            {d > 0 && <span className="text-sm text-slate-400 line-through">{fmtBRL(p.preco)}</span>}
            <span className="text-2xl font-extrabold text-emerald-600">{fmtBRL(precoFinal(p))}</span>
          </div>
          {p.descricao && <p className="text-sm text-slate-600 leading-relaxed mb-5">{p.descricao}</p>}
          {provadorOn && p.foto_url && (
            <button onClick={() => onProvar(p)} data-testid="detail-tryon"
              className="w-full py-3.5 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-xl flex items-center justify-center gap-2 mb-2 active:scale-[0.98] shadow-lg"><Sparkles className="w-5 h-5" /> Provar com IA</button>
          )}
          <div className="flex gap-2">
            <button onClick={() => onCart(p)} data-testid="detail-add-cart" className="flex-1 py-3 border border-slate-300 font-bold rounded-xl flex items-center justify-center gap-1.5 active:scale-[0.98]"><Plus className="w-4 h-4" /> Carrinho</button>
            <button onClick={() => onWhats(p)} data-testid="detail-whatsapp" className="flex-1 py-3 text-white font-bold rounded-xl flex items-center justify-center gap-1.5 active:scale-[0.98]" style={{ backgroundColor: "#25D366" }}><MessageCircle className="w-4 h-4" /> Comprar</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Try-On ---------------- */
function TryOnModal({ produto, lead, onClose }) {
  const [foto, setFoto] = useState(null);
  const [fotoPreview, setFotoPreview] = useState(null);
  const [termos, setTermos] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState(null);
  const camRef = useRef(null);
  const galRef = useRef(null);

  const escolher = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFoto(f); setResultado(null); setFotoPreview(URL.createObjectURL(f));
  };
  const provar = async () => {
    if (!foto) return;
    setLoading(true);
    const fd = new FormData();
    fd.append("produto_id", produto.id);
    fd.append("file", foto);
    if (lead?.id) fd.append("lead_id", lead.id);
    try {
      const r = await axios.post(`${API}/catalogo/public/provador`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResultado(r.data.imagem);
    } catch (err) {
      alert(err.response?.data?.detail || "Não foi possível gerar. Tente outra foto nítida e de frente.");
    } finally { setLoading(false); }
  };
  const dataURLtoFile = (durl) => {
    const [head, b64] = durl.split(",");
    const mime = head.match(/:(.*?);/)[1];
    const bin = atob(b64); const u8 = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
    return new File([u8], `prova-${produto.nome}.png`, { type: mime });
  };
  const salvar = () => {
    const a = document.createElement("a");
    a.href = resultado; a.download = `prova-${produto.nome}.png`; a.click();
  };
  const compartilhar = async () => {
    try {
      const file = dataURLtoFile(resultado);
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "Minha prova", text: `Provei o modelo ${produto.nome}!` });
      } else { salvar(); }
    } catch (e) { logDebug("Compartilhamento cancelado:", e); }
  };

  return (
    <div className="fixed inset-0 z-[55] bg-black/60 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl p-6 shadow-2xl relative max-h-[94vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="modal-tryon">
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-slate-900" data-testid="modal-tryon-close"><X className="w-6 h-6" /></button>
        <h2 className="text-center tracking-[0.2em] text-sm font-semibold text-slate-900 uppercase">Envie sua foto</h2>
        <p className="text-center text-xs text-slate-500 mt-1 mb-4">{produto.nome}</p>
        {!resultado ? (
          <>
            <div className="bg-slate-50 rounded-xl p-3 text-center text-xs text-slate-500 mb-4 flex items-center justify-center gap-2"><Sparkles className="w-4 h-4 text-amber-500" /> Foto nítida, de frente, boa iluminação.</div>
            <div className="relative w-full aspect-[3/4] max-w-[280px] mx-auto rounded-xl border-2 border-dashed border-slate-300 overflow-hidden flex items-center justify-center bg-slate-50 mb-4" data-testid="modal-tryon-upload">
              {fotoPreview ? <img src={fotoPreview} alt="sua foto" className="w-full h-full object-cover" /> : <div className="text-slate-300 flex flex-col items-center"><Camera className="w-12 h-12 mb-2" /><span className="text-xs">Sua foto aparece aqui</span></div>}
              {loading && <div className="absolute inset-0 bg-white/80 flex flex-col items-center justify-center gap-2"><Loader2 className="w-8 h-8 animate-spin text-slate-900" /><span className="text-xs font-medium text-slate-700">Provando os óculos...</span></div>}
            </div>
            <input ref={camRef} type="file" accept="image/*" capture="user" className="hidden" onChange={escolher} />
            <input ref={galRef} type="file" accept="image/*" className="hidden" onChange={escolher} />
            <div className="grid grid-cols-2 gap-3 mb-4">
              <button onClick={() => camRef.current?.click()} className="py-3 border border-slate-300 rounded-xl flex items-center justify-center gap-2 text-sm font-medium hover:border-slate-900" data-testid="button-tryon-camera"><Camera className="w-4 h-4" /> Tirar foto</button>
              <button onClick={() => galRef.current?.click()} className="py-3 border border-slate-300 rounded-xl flex items-center justify-center gap-2 text-sm font-medium hover:border-slate-900" data-testid="button-tryon-gallery"><ImageIcon className="w-4 h-4" /> Da galeria</button>
            </div>
            <label className="flex items-center justify-center gap-2 text-xs text-slate-500 mb-4 cursor-pointer">
              <input type="checkbox" checked={termos} onChange={(e) => setTermos(e.target.checked)} data-testid="checkbox-terms-tryon" className="w-4 h-4 rounded accent-slate-900" />
              Concordo que minha foto seja usada apenas para gerar a simulação
            </label>
            <button disabled={!foto || !termos || loading} onClick={provar} data-testid="button-submit-tryon"
              className="w-full py-3.5 bg-black hover:bg-slate-800 disabled:opacity-40 text-white font-bold rounded-xl shadow-lg active:scale-[0.98] tracking-widest uppercase text-sm">{loading ? "Gerando..." : "Provar óculos"}</button>
          </>
        ) : (
          <div className="text-center">
            <img src={resultado} alt="resultado" className="w-full max-w-sm mx-auto rounded-xl border border-slate-200" data-testid="tryon-resultado" />
            <p className="text-xs text-slate-400 mt-2">Simulação gerada por IA — pode não refletir o produto com exatidão.</p>
            <div className="grid grid-cols-2 gap-2 mt-4">
              <button onClick={compartilhar} data-testid="tryon-compartilhar" className="py-3 bg-slate-900 text-white rounded-xl font-bold flex items-center justify-center gap-2"><Share2 className="w-4 h-4" /> Compartilhar</button>
              <button onClick={salvar} data-testid="tryon-salvar" className="py-3 border border-slate-300 rounded-xl font-bold flex items-center justify-center gap-2"><Download className="w-4 h-4" /> Salvar</button>
            </div>
            <button onClick={() => { setResultado(null); setFoto(null); setFotoPreview(null); }} className="w-full mt-2 py-2.5 text-sm text-slate-500">Tentar outra foto</button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------- Cart Drawer ---------------- */
function CartDrawer({ cart, data, lead, onClose, onRemove, onEnviado }) {
  const total = cart.reduce((s, p) => s + precoFinal(p), 0);
  const enviar = () => {
    const linhas = cart.map((p) => `• ${p.nome}${p.codigo ? ` (cód. ${p.codigo})` : ""} — ${fmtBRL(precoFinal(p))}`).join("\n");
    const msg = `${data.mensagem_padrao}\n${linhas}\n\nTotal aprox.: ${fmtBRL(total)}${lead ? `\n\nCliente: ${lead.nome}` : ""}`;
    window.open(waLink(data.whatsapp, msg), "_blank");
    onEnviado();
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex justify-end" onClick={onClose}>
      <div className="bg-white w-full max-w-sm h-full flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="cart-drawer">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <h3 className="font-bold text-slate-900 flex items-center gap-2"><ShoppingBag className="w-5 h-5" /> Meu carrinho ({cart.length})</h3>
          <button onClick={onClose} className="text-slate-400"><X className="w-6 h-6" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {cart.length === 0 ? <p className="text-center text-slate-400 mt-10">Carrinho vazio.</p> : cart.map((p) => (
            <div key={p.id} className="flex items-center gap-3 border border-slate-100 rounded-xl p-2" data-testid={`cart-item-${p.id}`}>
              <div className="w-14 h-14 bg-white border border-slate-100 rounded-lg overflow-hidden flex items-center justify-center shrink-0">
                {p.foto_url ? <img src={`${BACKEND}${p.foto_url}`} alt="" className="w-full h-full object-contain" /> : <ImageIcon className="w-6 h-6 text-slate-200" />}
              </div>
              <div className="flex-1 min-w-0"><p className="text-sm font-semibold text-slate-800 truncate">{p.nome}</p><p className="text-sm font-bold text-emerald-600">{fmtBRL(precoFinal(p))}</p></div>
              <button onClick={() => onRemove(p.id)} className="text-red-500 p-1" data-testid={`cart-remove-${p.id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
        {cart.length > 0 && (
          <div className="p-4 border-t border-slate-100">
            <div className="flex justify-between text-sm mb-3"><span className="text-slate-500">Total aproximado</span><span className="font-extrabold text-slate-900">{fmtBRL(total)}</span></div>
            <button onClick={enviar} data-testid="cart-enviar-whatsapp" className="w-full py-3.5 text-white font-bold rounded-xl flex items-center justify-center gap-2 active:scale-[0.98]" style={{ backgroundColor: "#25D366" }}>
              <MessageCircle className="w-5 h-5" /> Enviar {cart.length} {cart.length === 1 ? "modelo" : "modelos"} no WhatsApp
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------- Page ---------------- */
export default function CatalogoPublico() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [erro, setErro] = useState(false);
  const [lead, setLead] = useState(null);
  const [genero, setGenero] = useState(null);
  const [ativa, setAtiva] = useState(null);
  const [tryOn, setTryOn] = useState(null);
  const [detalhe, setDetalhe] = useState(null);
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [busca, setBusca] = useState("");
  const [buscaOpen, setBuscaOpen] = useState(false);

  const leadKey = `catalogo_lead_${slug}`;

  useEffect(() => {
    const salvo = localStorage.getItem(leadKey);
    if (salvo) { try { setLead(JSON.parse(salvo)); } catch (e) { logDebug("Lead inválido no storage:", e); localStorage.removeItem(leadKey); } }
    axios.get(`${API}/catalogo/public/${slug}`).then((r) => setData(r.data)).catch(() => setErro(true));
  }, [slug, leadKey]);

  const registrarEvento = (produto_id, tipo) => {
    axios.post(`${API}/catalogo/public/evento`, { produto_id, tipo, lead_id: lead?.id }).catch((e) => logDebug("evento:", e));
  };
  const salvarLead = async (nome, telefone) => {
    const r = await axios.post(`${API}/catalogo/public/${slug}/lead`, { nome, telefone });
    const l = { id: r.data.lead_id, nome: r.data.nome, telefone: r.data.telefone };
    localStorage.setItem(leadKey, JSON.stringify(l));
    setLead(l);
  };
  const addCart = (p) => { setCart((c) => (c.find((x) => x.id === p.id) ? c : [...c, p])); setCartOpen(true); };
  const removeCart = (id) => setCart((c) => c.filter((x) => x.id !== id));
  const abrirDetalhe = (p) => { setDetalhe(p); registrarEvento(p.id, "view"); };
  const comprarWhats = (p) => {
    registrarEvento(p.id, "whatsapp");
    const msg = `${data.mensagem_padrao} *${p.nome}*${p.codigo ? ` (cód. ${p.codigo})` : ""} — ${fmtBRL(precoFinal(p))}${lead ? `\n\nCliente: ${lead.nome}` : ""}`;
    window.open(waLink(data.whatsapp, msg), "_blank");
  };
  const provarProduto = (p) => { setCartOpen(false); setDetalhe(null); setTryOn(p); };

  const categoriasOrdenadas = useMemo(() => (data ? ordenarCategorias(data.categorias, genero) : []), [data, genero]);
  const resultadosBusca = useMemo(() => {
    if (!busca.trim() || !data) return null;
    const q = busca.toLowerCase();
    const todos = data.categorias.flatMap((c) => c.produtos);
    return todos.filter((p) => (p.nome || "").toLowerCase().includes(q) || (p.categoria || "").toLowerCase().includes(q) || (p.codigo || "").toLowerCase().includes(q));
  }, [busca, data]);

  if (erro) return <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-500">Catálogo não encontrado.</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  if (!lead) return <LeadGate loja={data.loja} data={data} onSalvar={salvarLead} />;
  if (!genero) return <GeneroSelect data={data} onEscolher={(g) => { setGenero(g); setAtiva(null); }} />;

  const scrollCat = (nome) => { setAtiva(nome); document.getElementById(`cat-${nome}`)?.scrollIntoView({ behavior: "smooth", block: "start" }); };
  const cardProps = { noCarrinho: false, onProvar: provarProduto, onAbrir: abrirDetalhe, onCart: addCart, onWhats: comprarWhats, provadorOn: data?.provador_virtual !== false };

  return (
    <div className="h-[100dvh] overflow-y-auto bg-slate-50" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      {/* Header prominente */}
      <header className="bg-slate-900 text-white sticky top-0 z-40 shadow-lg">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-white text-slate-900 flex items-center justify-center shrink-0"><Glasses className="w-7 h-7" /></div>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-extrabold tracking-tight truncate" data-testid="store-header-title">{data.empresa ? `${data.empresa} · ` : ""}{data.loja}</h1>
            <div className="flex items-center gap-3 text-[11px] text-slate-300 flex-wrap">
              {data.horario && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{data.horario}</span>}
              {data.endereco && <span className="flex items-center gap-1 truncate"><MapPin className="w-3 h-3" />{data.endereco}</span>}
            </div>
          </div>
          <a href="/lojas" className="p-2 rounded-lg hover:bg-white/10 flex items-center gap-1 text-xs font-medium" data-testid="catalogo-outras-lojas" title="Ver outras lojas"><Store className="w-5 h-5" /><span className="hidden sm:inline">Outras lojas</span></a>
          <button onClick={() => setBuscaOpen((v) => !v)} className="p-2 rounded-lg hover:bg-white/10" data-testid="catalogo-busca-toggle"><Search className="w-5 h-5" /></button>
          <button onClick={() => setCartOpen(true)} className="relative p-2 rounded-lg hover:bg-white/10" data-testid="catalogo-cart-toggle">
            <ShoppingBag className="w-5 h-5" />
            {cart.length > 0 && <span className="absolute -top-0.5 -right-0.5 bg-emerald-500 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center" data-testid="cart-count">{cart.length}</span>}
          </button>
        </div>
        {buscaOpen && (
          <div className="max-w-5xl mx-auto px-4 pb-3">
            <input autoFocus value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar modelo ou categoria..." data-testid="catalogo-busca-input"
              className="w-full px-4 py-2.5 rounded-xl bg-white text-slate-900 text-sm outline-none" />
          </div>
        )}
        {/* Nav categorias */}
        {!resultadosBusca && categoriasOrdenadas.length > 0 && (
          <div className="flex gap-2 overflow-x-auto py-2.5 px-4 bg-slate-800" style={{ scrollbarWidth: "none" }}>
            <button onClick={() => setGenero(null)} className="whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-semibold bg-white/10 text-white" data-testid="genero-trocar">↺ {genero === "masc" ? "Masculino" : genero === "fem" ? "Feminino" : "Catálogo completo"}</button>
            {categoriasOrdenadas.map((c) => (
              <button key={c.nome} onClick={() => scrollCat(c.nome)} data-testid="category-nav-pill"
                className={`whitespace-nowrap px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${ativa === c.nome ? "bg-emerald-500 text-white" : "bg-white/10 text-slate-200"}`}>{c.nome} <span className="opacity-60">({c.total})</span></button>
            ))}
          </div>
        )}
      </header>

      <main className="max-w-5xl mx-auto px-4 py-5">
        {resultadosBusca ? (
          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-3">Resultados para "{busca}" ({resultadosBusca.length})</h2>
            {resultadosBusca.length === 0 ? <p className="text-slate-400 py-10 text-center">Nada encontrado.</p> : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="busca-resultados">
                {resultadosBusca.map((p) => <ProductCard key={p.id} p={p} {...cardProps} />)}
              </div>
            )}
          </section>
        ) : data.total_produtos === 0 ? (
          <div className="py-24 text-center text-slate-400"><ShoppingBag className="w-10 h-10 mx-auto mb-3" />Catálogo em breve.</div>
        ) : (
          categoriasOrdenadas.map((c) => (
            <section key={c.nome} id={`cat-${c.nome}`} className="mb-8 scroll-mt-36">
              <h2 className="text-lg font-bold tracking-tight text-slate-900 mb-3 flex items-center gap-2">{c.nome}<span className="text-sm font-normal text-slate-400">({c.total})</span></h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {c.produtos.map((p) => <ProductCard key={p.id} p={p} {...cardProps} />)}
              </div>
            </section>
          ))
        )}
        <footer className="text-center text-xs text-slate-400 pt-8 pb-28 border-t border-slate-200 mt-6">{data.empresa || "Ótica"} — {data.loja}. Ofertas de responsabilidade da loja.</footer>
      </main>

      {detalhe && <ProductDetail p={detalhe} onClose={() => setDetalhe(null)} onProvar={provarProduto} onCart={addCart} onWhats={comprarWhats} provadorOn={data?.provador_virtual !== false} />}
      {tryOn && <TryOnModal produto={tryOn} lead={lead} onClose={() => setTryOn(null)} />}
      {cartOpen && <CartDrawer cart={cart} data={data} lead={lead} onClose={() => setCartOpen(false)} onRemove={removeCart} onEnviado={() => { cart.forEach((p) => registrarEvento(p.id, "whatsapp")); setCartOpen(false); }} />}
    </div>
  );
}
